import React from 'react';
import { connect } from 'react-redux';
import AdminShell, { Icon } from './AdminShell';
import DataTable from './DataTable';
import { Drawer, Summary } from './Overlay';

/*
 * Audit log — layar `audit` paket client, sisi baca.
 *
 * Sisi tulisnya sudah ada sejak awal redesign (`src/libs/recordAudit.js`);
 * ini yang membuatnya bisa dilihat.
 *
 * TIDAK ADA TOMBOL SUNTING ATAU HAPUS, dan itu bukan kelalaian. Tidak ada
 * mutation yang mengubah baris tabel ini di mana pun, jadi tidak ada yang bisa
 * ditawarkan. Catatan yang bisa disunting bukan catatan.
 *
 * NAMA AKSI DITERJEMAHKAN, TAPI YANG TIDAK DIKENAL TETAP DITAMPILKAN APA
 * ADANYA. Daftar terjemahan di bawah diambil dari `AUDIT_ACTIONS` prototipe;
 * kalau suatu saat ada aksi baru yang belum ada di situ, ia muncul dengan nama
 * mentahnya, bukan hilang.
 *
 * PERAN YANG DITAMPILKAN ADALAH PERAN SAAT ITU. `actorRole` disalin ke baris
 * ketika ditulis, bukan diambil dari akun sekarang — supaya catatan tetap
 * benar setelah orangnya naik peran atau akunnya dihapus.
 */

const gql = (query, variables) => fetch('/graphql', {
  method: 'post',
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
  body: JSON.stringify({ query, variables }),
  credentials: 'include'
}).then(r => r.json());

const Q = `query ($currentPage: Int, $perPage: Int, $searchList: String,
                  $action: String, $actorEmail: String, $outcome: String) {
  getAuditLog(currentPage: $currentPage, perPage: $perPage, searchList: $searchList,
              action: $action, actorEmail: $actorEmail, outcome: $outcome) {
    status errorMessage count notSuccessCount actionsPresent actorsPresent
    results {
      id at actorEmail actorRole action targetType targetId targetLabel
      reason note metadata outcome ipAddress
    }
  }
}`;

const ok = res => res && Number(res.status) === 200;

// Diambil dari AUDIT_ACTIONS paket client.
const ACTIONS = {
  'booking.assign': { label: 'Booking assigned', ar: 'تم تعيين الطلب', kind: 'amber' },
  'booking.reassign': { label: 'Booking reassigned', ar: 'تمت إعادة تعيين الطلب', kind: 'amber' },
  'booking.cancel': { label: 'Booking cancelled', ar: 'تم إلغاء الطلب', kind: 'danger' },
  'booking.refund': { label: 'Booking refunded', ar: 'تم رد مبلغ الطلب', kind: 'danger' },
  'provider.invite': { label: 'Provider invited', ar: 'تمت دعوة مقدم الخدمة', kind: 'ok' },
  'provider.update': { label: 'Provider updated', ar: 'تم تحديث مقدم الخدمة', kind: 'amber' },
  'provider.call': { label: 'Provider call opened', ar: 'تم فتح اتصال مقدم الخدمة', kind: 'info' },
  'provider.approve': { label: 'Provider approved', ar: 'تمت الموافقة على مقدم الخدمة', kind: 'ok' },
  'provider.reject': { label: 'Provider rejected', ar: 'تم رفض مقدم الخدمة', kind: 'danger' },
  'provider.request_changes': { label: 'Changes requested', ar: 'طُلبت تعديلات', kind: 'warn' },
  'provider.ban': { label: 'Provider suspended', ar: 'تم تعليق مقدم الخدمة', kind: 'danger' },
  'review.remove': { label: 'Review removed', ar: 'تم حذف التقييم', kind: 'warn' },
  'customer.ban': { label: 'Customer banned', ar: 'تم حظر العميل', kind: 'danger' },
  'customer.delete': { label: 'Customer deleted', ar: 'تم حذف العميل', kind: 'danger' },
  'payout.release': { label: 'Payout released', ar: 'تم صرف الدفعة', kind: 'ok' },
  'payout.retry': { label: 'Payout retried', ar: 'أُعيدت محاولة الدفعة', kind: 'warn' },
  'wallet.adjust': { label: 'Wallet adjusted', ar: 'تم تعديل المحفظة', kind: 'amber' },
  'pricing.publish': { label: 'Pricing published', ar: 'تم نشر التسعير', kind: 'amber' },
  'page.create': { label: 'Page created', ar: 'تم إنشاء الصفحة', kind: 'ok' },
  'page.update': { label: 'Page updated', ar: 'تم تحديث الصفحة', kind: 'amber' },
  'precaution.update': { label: 'Precaution notice updated', ar: 'تم تحديث إشعار التنبيه', kind: 'amber' },
  'page.delete': { label: 'Page deleted', ar: 'تم حذف الصفحة', kind: 'danger' },
  'broadcast.send': { label: 'Broadcast sent', ar: 'تم إرسال الإشعار الجماعي', kind: 'danger' },
  'admin.create': { label: 'Admin created', ar: 'تم إنشاء مسؤول', kind: 'amber' },
  'admin.update': { label: 'Admin changed', ar: 'تم تعديل المسؤول', kind: 'amber' },
  'admin.remove': { label: 'Admin removed', ar: 'تم حذف المسؤول', kind: 'danger' },
  'admin.role_create': { label: 'Role created', ar: 'تم إنشاء الدور', kind: 'amber' },
  'admin.role_change': { label: 'Role changed', ar: 'تم تعديل الدور', kind: 'danger' },
  'admin.role_remove': { label: 'Role deleted', ar: 'تم حذف الدور', kind: 'danger' },
  'admin.password_reset': { label: 'Admin password reset', ar: 'تمت إعادة تعيين كلمة مرور المسؤول', kind: 'danger' },
  'settings.update': { label: 'Settings changed', ar: 'تم تعديل الإعدادات', kind: 'amber' },
  'privilege.denied': { label: 'Permission denied', ar: 'رُفضت الصلاحية', kind: 'danger' },
  'auth.login': { label: 'Signed in', ar: 'تم تسجيل الدخول', kind: '' },
  'auth.login_failed': { label: 'Sign-in failed', ar: 'فشل تسجيل الدخول', kind: 'danger' }
};

const meta = (action, arabic) => {
  const item = ACTIONS[action] || { label: action, ar: action, kind: '' };
  return { ...item, label: arabic ? item.ar : item.label };
};

const when = (iso, arabic) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(arabic ? 'ar-AE' : 'en-GB', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Dubai'
  }).format(d);
};

// metadata tersimpan sebagai teks JSON; ditampilkan sebagai pasangan.
const metaPairs = (raw, arabic) => {
  if (!raw) return [];
  try {
    const o = JSON.parse(raw);
    if (!o || typeof o !== 'object') return [[arabic ? 'السياق' : 'Context', String(raw)]];
    return Object.keys(o).map(k => [k, o[k] === null
      ? '—'
      : (typeof o[k] === 'object' ? JSON.stringify(o[k]) : String(o[k]))]);
  } catch (e) {
    return [[arabic ? 'السياق' : 'Context', String(raw)]];
  }
};

const csvCell = value => {
  let text = String(value === null || value === undefined ? '' : value);
  if (/^\s*[=+\-@]/.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
};

class AuditLog extends React.Component {
  state = {
    rows: [], count: 0, notSuccess: 0, actions: [], actors: [],
    page: 1, perPage: 25, search: '', action: '', actor: '', outcome: '',
    loading: true, exporting: false, error: null, exportError: null, viewing: null
  };

  componentDidMount() { this.load(); }

  isArabic = () => String(this.props.currentLocale || '').startsWith('ar');
  tx = (en, ar) => this.isArabic() ? ar : en;

  tableCopy = () => this.isArabic() ? {
    showing: (from, to, total) => `عرض ${from}–${to} من ${Number(total).toLocaleString('ar-AE')}`,
    sortedPage: 'الترتيب داخل هذه الصفحة', previousPage: 'الصفحة السابقة', nextPage: 'الصفحة التالية',
    rowsPerPage: 'عدد الصفوف في الصفحة', perPage: n => `${n} / صفحة`, rtl: true,
    nothingShow: 'لا توجد سجلات تدقيق'
  } : {
    showing: (from, to, total) => `Showing ${from}–${to} of ${Number(total).toLocaleString('en-US')}`,
    sortedPage: 'sorted within this page', previousPage: 'Previous page', nextPage: 'Next page',
    rowsPerPage: 'Rows per page', perPage: n => `${n} / page`, rtl: false,
    nothingShow: 'No audit entries'
  };

  load = async () => {
    const { page, perPage, search, action, actor, outcome } = this.state;
    this.setState({ loading: true, error: null });
    const json = await gql(Q, {
      currentPage: page,
      perPage,
      searchList: search || null,
      action: action || null,
      actorEmail: actor || null,
      outcome: outcome || null
    }).catch(() => null);
    const res = json && json.data && json.data.getAuditLog;
    if (!ok(res)) {
      this.setState({ loading: false, error: this.tx(
        (res && res.errorMessage) || 'Could not load the audit log.', 'تعذّر تحميل سجل التدقيق.') });
      return;
    }
    this.setState({
      rows: res.results || [],
      count: res.count || 0,
      notSuccess: res.notSuccessCount || 0,
      actions: res.actionsPresent || [],
      actors: res.actorsPresent || [],
      loading: false
    });
  };

  setPage = page => this.setState({ page }, this.load);
  setPerPage = perPage => this.setState({ perPage, page: 1 }, this.load);
  setSearch = search => this.setState({ search, page: 1 }, this.load);
  setFilter = (k, v) => this.setState({ [k]: v, page: 1 }, this.load);

  /*
   * Ekspor mengambil ulang dari server dengan penyaring yang sama, bukan
   * menyalin halaman yang sedang terlihat — berkas yang berisi 25 baris
   * padahal lognya 4.000 baris adalah berkas yang menyesatkan.
   */
  exportCsv = async () => {
    const { search, action, actor, outcome, count } = this.state;
    this.setState({ exporting: true, exportError: null });
    const json = await gql(Q, {
      currentPage: 1,
      perPage: 100,
      searchList: search || null,
      action: action || null,
      actorEmail: actor || null,
      outcome: outcome || null
    }).catch(() => null);
    const res = json && json.data && json.data.getAuditLog;
    this.setState({ exporting: false });
    if (!ok(res)) {
      this.setState({ exportError: this.tx('Could not export the audit log.', 'تعذّر تصدير سجل التدقيق.') });
      return;
    }

    const head = this.isArabic()
      ? ['الوقت', 'المنفذ', 'الدور وقتها', 'الإجراء', 'نوع الهدف', 'الهدف', 'السبب', 'النتيجة', 'IP', 'السياق']
      : ['When', 'Actor', 'Role at the time', 'Action', 'Target type',
        'Target', 'Reason', 'Outcome', 'IP', 'Context'];
    const body = (res.results || []).map(r => [
      r.at, r.actorEmail, r.actorRole, r.action, r.targetType,
      r.targetLabel, r.reason, r.outcome, r.ipAddress, r.metadata
    ].map(csvCell).join(','));

    const note = count > 100
      ? `\n${csvCell(this.tx(
        `Note: this export holds the newest 100 of ${count} matching entries.`,
        `ملاحظة: يحتوي هذا التصدير على أحدث 100 سجل من أصل ${count} نتيجة مطابقة.`))}`
      : '';
    const blob = new Blob([`${head.map(csvCell).join(',')}\n${body.join('\n')}${note}`],
      { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `bas-audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 0);
  };

  columns() {
    const arabic = this.isArabic();
    const outcomeLabel = value => ({
      success: this.tx('Success', 'نجاح'),
      denied: this.tx('Denied', 'مرفوض'),
      failure: this.tx('Failure', 'فشل')
    }[value] || value);
    return [
      {
        key: 'at', label: this.tx('When', 'الوقت'), sortable: true,
        render: r => <span className="mono" style={{ fontSize: 'var(--fs-xs)' }}>{when(r.at, arabic)}</span>
      },
      {
        key: 'actorEmail', label: this.tx('Actor', 'المنفذ'), sortable: true,
        render: r => (
          <div>
            <div style={{ fontWeight: 700 }}>{r.actorEmail}</div>
            {r.actorRole && (
              <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>
                {this.tx(`${r.actorRole} at the time`, `${r.actorRole} وقتها`)}
              </div>
            )}
          </div>
        )
      },
      {
        key: 'action', label: this.tx('Action', 'الإجراء'), sortable: true,
        render: r => {
          const m = meta(r.action, arabic);
          return <span className={`tag ${m.kind}`}><i />{m.label}</span>;
        }
      },
      {
        key: 'targetLabel', label: this.tx('Target', 'الهدف'),
        render: r => (
          <div>
            <div dir="auto">{r.targetLabel || '—'}</div>
            {r.targetType && (
              <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{r.targetType}</div>
            )}
          </div>
        )
      },
      {
        key: 'reason', label: this.tx('Reason', 'السبب'),
        render: r => (r.reason ? <span dir="auto">{r.reason}</span> : <span className="muted">—</span>)
      },
      {
        key: 'outcome', label: this.tx('Outcome', 'النتيجة'),
        render: r => (
          <span className={`tag ${r.outcome === 'success' ? 'ok' : (r.outcome === 'denied' ? 'danger' : 'warn')}`}>
            <i />{outcomeLabel(r.outcome)}
          </span>
        )
      },
      {
        key: 'act', label: '', align: 'end',
        render: r => (
          <button className="btn icon" type="button" title={this.tx('View full entry', 'عرض السجل كاملاً')}
            aria-label={this.tx(`View entry ${r.id}`, `عرض السجل ${r.id}`)}
            onClick={() => this.setState({ viewing: r })}>
            <Icon name="eye" />
          </button>
        )
      }
    ];
  }

  render() {
    const {
      rows, count, notSuccess, actions, actors, page, perPage,
      action, actor, outcome, loading, exporting, error, exportError, viewing
    } = this.state;

    return (
      <AdminShell
        current="audit"
        breadcrumb={[this.tx('System', 'النظام'), this.tx('Audit log', 'سجل التدقيق')]}
        title={this.tx('Audit log', 'سجل التدقيق')}
        lead={this.tx('Append-only record of privileged actions. Rows cannot be edited or deleted.',
          'سجل دائم للإجراءات الحساسة، ولا يمكن تعديل صفوفه أو حذفها.')}
        actions={[
          <button className="btn ghost" type="button" key="x" onClick={this.exportCsv}
            disabled={loading || exporting || count === 0}>
            <Icon name="dl" />{exporting ? this.tx('Exporting…', 'جارٍ التصدير…') : this.tx('Export CSV', 'تصدير CSV')}
          </button>
        ]}
      >
        {/*
          * Yang TIDAK tercatat sama pentingnya dengan yang tercatat. Log yang
          * dianggap lengkap padahal berlubang lebih berbahaya daripada tidak
          * ada log sama sekali, karena ketiadaan baris dibaca sebagai bukti.
          */}
        <div className="notice warn">
          <span className="ni">!</span>
          <span className="nt">
            {this.tx(
              'Not everything is recorded yet. Refunds, cancellations, assignments, payouts, provider and customer decisions, broadcasts and admin or role changes are. Sign-ins, pricing edits, most content changes and most permission refusals are not — an action missing from this list does not mean it did not happen.',
              'لم تُسجّل كل الإجراءات بعد. تُسجّل عمليات رد المبالغ والإلغاء والتعيين والدفعات وقرارات مقدمي الخدمة والعملاء والإشعارات الجماعية وتغييرات المسؤولين والأدوار. لا تُسجّل عمليات تسجيل الدخول وتعديلات التسعير ومعظم تغييرات المحتوى ومعظم حالات رفض الصلاحيات؛ غياب الإجراء من القائمة لا يعني أنه لم يحدث.')}
          </span>
        </div>

        {notSuccess > 0 && (
          <div className="notice">
            <span className="ni">i</span>
            <span className="nt">
              {this.tx(
                `${notSuccess} entr${notSuccess === 1 ? 'y' : 'ies'} recorded a failure or refusal. Set Outcome to “denied” to see checks that blocked someone.`,
                `سجّل ${notSuccess} إدخال حالة فشل أو رفض. اختر النتيجة «مرفوض» لرؤية عمليات التحقق التي منعت إجراءً.`)}
            </span>
          </div>
        )}

        {exportError && (
          <div className="notice danger">
            <span className="ni">!</span><span className="nt">{exportError}</span>
            <button className="btn ghost sm" type="button"
              onClick={() => this.setState({ exportError: null })}>{this.tx('Dismiss', 'إغلاق')}</button>
          </div>
        )}

        {error
          ? <div className="notice danger"><span className="ni">!</span><span className="nt">{error}</span></div>
          : (
            <DataTable
              columns={this.columns()}
              rows={rows}
              count={count}
              page={page}
              perPage={perPage}
              loading={loading}
              onPage={this.setPage}
              onPerPage={this.setPerPage}
              onSearch={this.setSearch}
              searchPlaceholder={this.tx('Search actor, target, reason or action', 'ابحث بالمنفذ أو الهدف أو السبب أو الإجراء')}
              filters={
                <React.Fragment>
                  {/* Hanya menawarkan aksi dan pelaku yang benar-benar ada. */}
                  <select className="inp" value={action} aria-label={this.tx('Filter by action', 'التصفية حسب الإجراء')}
                    onChange={e => this.setFilter('action', e.target.value)}>
                    <option value="">{this.tx('Action: all', 'الإجراء: الكل')}</option>
                    {actions.map(a => <option key={a} value={a}>{meta(a, this.isArabic()).label}</option>)}
                  </select>
                  <select className="inp" value={actor} aria-label={this.tx('Filter by actor', 'التصفية حسب المنفذ')}
                    onChange={e => this.setFilter('actor', e.target.value)}>
                    <option value="">{this.tx('Actor: all', 'المنفذ: الكل')}</option>
                    {actors.map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                  <select className="inp" value={outcome} aria-label={this.tx('Filter by outcome', 'التصفية حسب النتيجة')}
                    onChange={e => this.setFilter('outcome', e.target.value)}>
                    <option value="">{this.tx('Outcome: all', 'النتيجة: الكل')}</option>
                    <option value="success">{this.tx('Success', 'نجاح')}</option>
                    <option value="denied">{this.tx('Denied', 'مرفوض')}</option>
                    <option value="failure">{this.tx('Failure', 'فشل')}</option>
                  </select>
                </React.Fragment>
              }
              copy={this.tableCopy()}
              emptyText={this.tx('No audit entries', 'لا توجد سجلات تدقيق')}
              emptyHint={this.tx('Privileged actions are recorded here as they happen.', 'ستظهر الإجراءات الحساسة هنا عند حدوثها.')}
            />
          )}

        <div style={{ marginTop: 16, fontSize: 'var(--fs-xs)', color: 'var(--tx-3)', maxWidth: '80ch' }}>
          {this.tx(
            'Entries are append-only. There is deliberately no update or delete path. Sensitive keys — anything named like a password, token or card number — are redacted recursively before a row is written.',
            'تُضاف السجلات فقط، ولا يوجد مسار لتعديلها أو حذفها. تُحجب المفاتيح الحساسة مثل كلمات المرور والرموز وأرقام البطاقات، حتى داخل البيانات المتداخلة، قبل كتابة السجل.')}
        </div>

        {viewing && (
          <Drawer
            title={meta(viewing.action, this.isArabic()).label}
            sub={`${viewing.actorEmail} · ${when(viewing.at, this.isArabic())}`}
            onClose={() => this.setState({ viewing: null })}
          >
            <Summary pairs={[
              [this.tx('When', 'الوقت'), when(viewing.at, this.isArabic())],
              [this.tx('Actor', 'المنفذ'), viewing.actorEmail],
              [this.tx('Role at the time', 'الدور وقتها'), viewing.actorRole || '—'],
              [this.tx('Action', 'الإجراء'), viewing.action],
              [this.tx('Outcome', 'النتيجة'), this.tx(
                viewing.outcome === 'success' ? 'Success' : (viewing.outcome === 'denied' ? 'Denied' : 'Failure'),
                viewing.outcome === 'success' ? 'نجاح' : (viewing.outcome === 'denied' ? 'مرفوض' : 'فشل'))],
              [this.tx('Target', 'الهدف'), viewing.targetLabel || '—'],
              [this.tx('Target type', 'نوع الهدف'), viewing.targetType || '—'],
              [this.tx('Target id', 'معرّف الهدف'), viewing.targetId || '—'],
              [this.tx('Reason', 'السبب'), viewing.reason || '—'],
              [this.tx('IP address', 'عنوان IP'), viewing.ipAddress || '—']
            ]} />

            {viewing.note && (
              <div style={{ marginTop: 14 }}>
                <b>{this.tx('Note', 'ملاحظة')}</b>
                <p dir="auto">{viewing.note}</p>
              </div>
            )}

            {metaPairs(viewing.metadata, this.isArabic()).length > 0 && (
              <div style={{ marginTop: 14 }}>
                <b>{this.tx('Context', 'السياق')}</b>
                <Summary pairs={metaPairs(viewing.metadata, this.isArabic())} />
              </div>
            )}
          </Drawer>
        )}
      </AdminShell>
    );
  }
}

const mapState = state => ({ currentLocale: state && state.intl && state.intl.locale });

export default connect(mapState)(AuditLog);
